import { PlusOutlined } from '@ant-design/icons';
import type { InputRef } from 'antd';
import { Input, Tag, Tooltip } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { isPresent } from "@Utils";

import "@Styles/components/tokenisedInput.scss";

interface IProps {
    handleOnChange?: (newTags: string[]) => void;
    handleSelect?: (tag: string) => void;

    disabled?: boolean;
    initialOptions?: string[];
    maxOptions?: number;
    maxTagLength?: number;
    selectedTag?: string;
}

const TokenisedInput: React.FC<IProps> = ({
    handleOnChange,
    handleSelect,

    disabled,
    initialOptions,
    maxOptions,
    maxTagLength = 20,
    selectedTag: initiallySelectedTag,
}) => {
    const [tags, setTags] = useState<string[]>(initialOptions ?? []);
    const [selectedTag, setSelectedTag] = useState(initiallySelectedTag);
    const [inputVisible, setInputVisible] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const [editInputIndex, setEditInputIndex] = useState(-1);
    const [editInputValue, setEditInputValue] = useState("");
    const inputRef = useRef<InputRef>(null);
    const editRef = useRef<InputRef>(null);

    useEffect(() => {
        if (inputVisible) {
            inputRef.current?.focus();
        }
    }, [inputVisible]);

    useEffect(() => {
        editRef.current?.focus();
    }, [inputValue]);

    const handleClose = (removedTag: string) => {
        const newTags = tags.filter((tag) => tag !== removedTag);
        setTags(newTags);
        if (handleOnChange !== undefined) handleOnChange(newTags);
    };

    const showInput = () => {
        setInputVisible(true);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
    };

    const handleInputConfirm = () => {
        if (inputValue && tags.indexOf(inputValue) === -1) {
            const newTags = [...tags, inputValue];
            setTags(newTags);
            if (handleOnChange !== undefined) handleOnChange(newTags);
        }
        setInputVisible(false);
        setInputValue("");
    };

    const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEditInputValue(e.target.value);
    };

    const handleEditInputConfirm = () => {
        const newTags = [...tags];
        newTags[editInputIndex] = editInputValue;
        setTags(newTags);
        if (handleOnChange !== undefined) handleOnChange(newTags);
        setEditInputIndex(-1);
        setInputValue("");
    };

    const isCheckable = isPresent(handleSelect);

    return (
        <div className={"tokenised-input"}>
            {tags.map((tag, index) => {
                if (editInputIndex === index) {
                    return (
                        <Input
                            ref={editRef}
                            key={tag}
                            size="small"
                            className="tag-input"
                            value={editInputValue}
                            onChange={handleEditInputChange}
                            onBlur={handleEditInputConfirm}
                            onPressEnter={handleEditInputConfirm}
                        />
                    );
                }

                const isLongTag = tag.length > maxTagLength;

                const tagElem = (
                    <Tag
                        className={`edit-tag ${
                            !!selectedTag && selectedTag === tag ? "ant-tag-checkable-checked" : ""
                        }`}
                        closable={true}
                        key={tag}
                        onClick={() => {
                            if (isCheckable) {
                                setSelectedTag(tag);
                                handleSelect(tag);
                            }
                        }}
                        onClose={() => handleClose(tag)}
                        visible={!disabled}
                    >
                        <span
                            onDoubleClick={(e) => {
                                if (index !== 0) {
                                    setEditInputIndex(index);
                                    setEditInputValue(tag);
                                    e.preventDefault();
                                }
                            }}
                        >
                            {isLongTag ? `${tag.slice(0, maxTagLength)}...` : tag}
                        </span>
                    </Tag>
                );
                return isLongTag ? (
                    <Tooltip title={tag} key={tag}>
                        {tagElem}
                    </Tooltip>
                ) : (
                    tagElem
                );
            })}
            {inputVisible && tags.length < (maxOptions ?? 50) && (
                <Input
                    disabled={disabled}
                    ref={inputRef}
                    type="text"
                    size="small"
                    className="tag-input"
                    value={inputValue}
                    onChange={handleInputChange}
                    onBlur={handleInputConfirm}
                    onPressEnter={handleInputConfirm}
                />
            )}
            {!inputVisible && (
                <Tag visible={!disabled} className="site-tag-plus" onClick={showInput}>
                    <PlusOutlined /> New
                </Tag>
            )}
        </div>
    );
};

export default TokenisedInput;
